/**
 * Page targeting for provider acquisition popups (public visitors).
 */

export const DEFAULT_PROVIDER_ACQUISITION_PAGES_TO_SHOW = [
  '/',
  '/services',
  '/training-provider-services',
  '/about',
  '/events',
  '/events/*',
  '/blog',
  '/blog/*',
  '/list-training',
  '/list-training/*',
] as const;

export const DEFAULT_PROVIDER_ACQUISITION_PAGES_TO_EXCLUDE = [
  '/provider-signup',
  '/provider-signup/*',
  '/signup',
  '/signup/*',
  '/login',
  '/login/*',
  '/auth/*',
  '/backoffice/*',
  '/embed/*',
  '/dashboard/*',
  '/k9-ops/*',
  '*/register*',
  '*/checkout*',
] as const;

/**
 * Normalize a pathname for targeting (strip query/hash, ensure leading slash).
 */
export function normalizePagePath(pageUrl: string): string {
  try {
    if (pageUrl.startsWith('http')) {
      return new URL(pageUrl).pathname || '/';
    }
  } catch {
    // fall through
  }
  const path = pageUrl.split('?')[0]?.split('#')[0] ?? '/';
  return path.startsWith('/') ? path : `/${path}`;
}

/**
 * Check if pathname matches a glob pattern (* = any segment).
 */
export function matchesPagePattern(pathname: string, pattern: string): boolean {
  const normalized = normalizePagePath(pathname);
  const regexPattern = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*')
    .replace(/\?/g, '\\?');
  return new RegExp(`^${regexPattern}$`, 'i').test(normalized);
}

export function isPageTargetedForProviderAcquisition(
  pageUrl: string,
  pagesToShow?: string[] | null,
  pagesToExclude?: string[] | null,
): boolean {
  const pathname = normalizePagePath(pageUrl);
  const exclude = pagesToExclude?.length ? pagesToExclude : [...DEFAULT_PROVIDER_ACQUISITION_PAGES_TO_EXCLUDE];
  for (const pattern of exclude) {
    if (matchesPagePattern(pathname, pattern)) {
      return false;
    }
  }
  const include = pagesToShow?.length ? pagesToShow : [...DEFAULT_PROVIDER_ACQUISITION_PAGES_TO_SHOW];
  for (const pattern of include) {
    if (matchesPagePattern(pathname, pattern)) {
      return true;
    }
  }
  return false;
}

export function isProviderAcquisitionPathExcluded(pathname: string): boolean {
  return !isPageTargetedForProviderAcquisition(pathname, [...DEFAULT_PROVIDER_ACQUISITION_PAGES_TO_SHOW], [
    ...DEFAULT_PROVIDER_ACQUISITION_PAGES_TO_EXCLUDE,
  ]);
}
